/**
 * Variant resolution for storefront cart/checkout.
 * Attributes must match product_variants.attributes (JSONB) with lexicographically ordered keys.
 */

import {
  normalizeAttributes,
  sanitizeStorefrontSelection,
  variantAttributesMatchSelection,
} from './attributes'
import type { NormalizedProduct, NormalizedVariant } from './product'

export {
  attributesEqual,
  attributesKey,
  normalizeAttributes,
  sanitizeStorefrontSelection,
  stripInternalAttributeKeys,
  variantAttributesMatchSelection,
} from './attributes'

function isNonEmptySelection(selected: Record<string, string>): boolean {
  return Object.keys(sanitizeStorefrontSelection(selected)).length > 0
}

export type ResolveVariantErrorCode =
  | 'simple_product'
  | 'no_default'
  | 'invalid_default'
  | 'no_match'
  | 'inactive_variant'

export type ResolveVariantResult =
  | {
      ok: true
      /** null when product has no variants (checkout line uses product price only). */
      variantId: string | null
      variant: NormalizedVariant | null
    }
  | {
      ok: false
      code: ResolveVariantErrorCode
      message: string
    }

/**
 * Resolve storefront selection to a single variant.
 *
 * Rules:
 * - Simple product (`!hasVariants`): returns variantId null (backend uses product.price / product.stock rules).
 * - Variable product, empty `selectedOptions`: fallback to `defaultVariantId` / default-flag variant.
 * - Variable product, non-empty `selectedOptions`: each selected key must match the variant; extra internal keys (e.g. `__ae_matrix`) on the variant are ignored.
 */
export function resolveVariant(
  product: NormalizedProduct,
  selectedOptions: Record<string, string>
): ResolveVariantResult {
  if (!product.hasVariants) {
    return {
      ok: true,
      variantId: null,
      variant: null,
    }
  }

  // "Selectable" = active + out_of_stock (visible on PDP, may be unpurchasable).
  // Archived/inactive variants are fully hidden.
  const selectableVariants = product.variants.filter(
    (v) => v.status !== 'archived' && v.status !== 'inactive'
  )
  // Only truly active variants can be added to cart.
  const activeVariants = product.variants.filter((v) => v.status === 'active')

  if (!isNonEmptySelection(selectedOptions)) {
    // Priority: defaultVariantId → isDefault flag → first selectable variant.
    // We intentionally search selectableVariants (not just activeVariants) so that
    // an out_of_stock default variant still resolves and displays its price.
    if (product.defaultVariantId) {
      const def = selectableVariants.find((v) => v.id === product.defaultVariantId)
      if (def) return { ok: true, variantId: def.id, variant: def }
    }
    const flagged = selectableVariants.find((v) => v.isDefault)
    if (flagged) return { ok: true, variantId: flagged.id, variant: flagged }

    // Last resort: use the first selectable variant so the page is never broken.
    if (selectableVariants[0]) {
      return { ok: true, variantId: selectableVariants[0].id, variant: selectableVariants[0] }
    }
    return {
      ok: false,
      code: 'no_default',
      message: 'Produit à variantes : aucune variante par défaut configurée.',
    }
  }

  // Explicit selection — only match active variants (out_of_stock variants can't be purchased).
  const wanted = sanitizeStorefrontSelection(selectedOptions)
  const match = activeVariants.find((v) =>
    variantAttributesMatchSelection((v.attributes || {}) as Record<string, string>, wanted),
  )
  if (!match) {
    const oosMatch = selectableVariants.find((v) =>
      variantAttributesMatchSelection((v.attributes || {}) as Record<string, string>, wanted),
    )
    if (oosMatch) {
      return { ok: true, variantId: oosMatch.id, variant: oosMatch }
    }
    return {
      ok: false,
      code: 'no_match',
      message: 'Aucune variante ne correspond aux options sélectionnées.',
    }
  }

  return { ok: true, variantId: match.id, variant: match }
}
